import type { Card } from "@/context/AppContext";
import { normalizePokemonName } from "@/features/cardScoringEngine";
import type { DeckInventoryMatch, ParsedDeckLine } from "@/types/deckBuilder";

interface MatcherOptions {
  /** Allow set-substitution for Trainers / non-evolution Pokémon. Default true (casual decks). */
  allowSetSubstitution?: boolean;
  /** Generic basic-energy matching by type name. Default true. */
  allowEnergyTypeMatching?: boolean;
}

/**
 * Match a parsed deck list against the seller's inventory. Returns one
 * DeckInventoryMatch per parsed line.
 *
 * Match priority (per spec):
 *   1. exact name + setCode + cardNumber  → confidence 1.0
 *   2. exact name + setCode               → 0.9
 *   3. exact normalized name              → 0.8
 *   4. fuzzy normalized name (contains)   → 0.6
 *   5. basic-energy generic match by type → 0.7
 *
 * Inventory cards are filtered to listingStatus === "Unlisted" with quantity > 0.
 * Cards already claimed by an earlier line are excluded from later lines so
 * the same physical card isn't double-counted.
 */
export function matchDeckListToInventory(
  parsedLines: ParsedDeckLine[],
  inventoryCards: Card[],
  options: MatcherOptions = {},
): DeckInventoryMatch[] {
  const allowSetSub = options.allowSetSubstitution ?? true;
  const allowEnergyType = options.allowEnergyTypeMatching ?? true;

  const pool = inventoryCards
    .filter((c) => c.listingStatus === "Unlisted" && (c.quantity ?? 0) > 0)
    .map((c) => ({
      card: c,
      remaining: c.quantity,
      normalizedName: normalizePokemonName(c.name),
    }));

  const results: DeckInventoryMatch[] = [];
  for (const line of parsedLines) {
    const result = matchLine(line, pool, { allowSetSub, allowEnergyType });
    results.push(result);
  }
  return results;
}

interface PoolEntry {
  card: Card;
  remaining: number;
  normalizedName: string;
}

function matchLine(
  line: ParsedDeckLine,
  pool: PoolEntry[],
  opts: { allowSetSub: boolean; allowEnergyType: boolean },
): DeckInventoryMatch {
  const want = line.quantity;
  const normalizedRequested = normalizePokemonName(line.name);
  const isBasicEnergy = looksLikeBasicEnergy(line.name);

  const matchedCards: Card[] = [];
  const selectedCardIds: string[] = [];
  let owned = 0;
  let bestConfidence = 0;
  let matchReason = "No match";

  // Tier 1 — exact name + setCode + cardNumber.
  if (line.setCode && line.cardNumber) {
    for (const entry of pool) {
      if (owned >= want) break;
      if (entry.remaining <= 0) continue;
      if (
        entry.card.setCode === line.setCode &&
        entry.card.cardNumber === line.cardNumber &&
        entry.normalizedName === normalizedRequested
      ) {
        const take = Math.min(entry.remaining, want - owned);
        matchedCards.push(entry.card);
        selectedCardIds.push(entry.card.id);
        entry.remaining -= take;
        owned += take;
        bestConfidence = Math.max(bestConfidence, 1.0);
        matchReason = "Exact set + number match";
      }
    }
  }

  // Tier 2 — exact name + setCode.
  if (owned < want && line.setCode) {
    for (const entry of pool) {
      if (owned >= want) break;
      if (entry.remaining <= 0) continue;
      if (selectedCardIds.includes(entry.card.id)) continue;
      if (
        entry.card.setCode === line.setCode &&
        entry.normalizedName === normalizedRequested
      ) {
        const take = Math.min(entry.remaining, want - owned);
        matchedCards.push(entry.card);
        selectedCardIds.push(entry.card.id);
        entry.remaining -= take;
        owned += take;
        bestConfidence = Math.max(bestConfidence, 0.9);
        if (matchReason === "No match") matchReason = "Exact name + set match";
      }
    }
  }

  // Tier 3 — exact normalized name (any set).
  if (owned < want) {
    const allowSetSub = opts.allowSetSub || !line.setCode;
    if (allowSetSub) {
      for (const entry of pool) {
        if (owned >= want) break;
        if (entry.remaining <= 0) continue;
        if (selectedCardIds.includes(entry.card.id)) continue;
        if (entry.normalizedName === normalizedRequested) {
          const take = Math.min(entry.remaining, want - owned);
          matchedCards.push(entry.card);
          selectedCardIds.push(entry.card.id);
          entry.remaining -= take;
          owned += take;
          bestConfidence = Math.max(bestConfidence, 0.8);
          if (
            matchReason === "No match" ||
            matchReason === "Exact name + set match"
          ) {
            matchReason = "Name match (set substituted)";
          }
        }
      }
    }
  }

  // Tier 4 — basic energy by type name.
  if (owned < want && opts.allowEnergyType && isBasicEnergy) {
    const energyType = extractEnergyType(line.name);
    if (energyType) {
      for (const entry of pool) {
        if (owned >= want) break;
        if (entry.remaining <= 0) continue;
        if (selectedCardIds.includes(entry.card.id)) continue;
        if (cardLooksLikeEnergy(entry.card, energyType)) {
          const take = Math.min(entry.remaining, want - owned);
          matchedCards.push(entry.card);
          selectedCardIds.push(entry.card.id);
          entry.remaining -= take;
          owned += take;
          bestConfidence = Math.max(bestConfidence, 0.7);
          if (matchReason === "No match") {
            matchReason = `Generic ${energyType} energy match`;
          }
        }
      }
    }
  }

  // Tier 5 — fuzzy contains match.
  if (owned < want) {
    for (const entry of pool) {
      if (owned >= want) break;
      if (entry.remaining <= 0) continue;
      if (selectedCardIds.includes(entry.card.id)) continue;
      if (
        entry.normalizedName.includes(normalizedRequested) ||
        normalizedRequested.includes(entry.normalizedName)
      ) {
        const take = Math.min(entry.remaining, want - owned);
        matchedCards.push(entry.card);
        selectedCardIds.push(entry.card.id);
        entry.remaining -= take;
        owned += take;
        bestConfidence = Math.max(bestConfidence, 0.6);
        if (matchReason === "No match") matchReason = "Fuzzy name match";
      }
    }
  }

  // Build substitutes list (cards in pool that share normalized name but
  // weren't picked because we ran out of demand or had higher-tier matches).
  const substitutes: Card[] = [];
  if (owned < want) {
    for (const entry of pool) {
      if (entry.remaining <= 0) continue;
      if (selectedCardIds.includes(entry.card.id)) continue;
      if (
        entry.normalizedName === normalizedRequested ||
        entry.normalizedName.includes(normalizedRequested)
      ) {
        substitutes.push(entry.card);
        if (substitutes.length >= 5) break;
      }
    }
  }

  return {
    requested: line,
    matchedCards,
    selectedCardIds,
    ownedQty: owned,
    requestedQty: want,
    missingQty: Math.max(0, want - owned),
    substitutes,
    matchConfidence: bestConfidence,
    matchReason,
  };
}

const BASIC_ENERGY_TYPES = [
  "fire",
  "water",
  "grass",
  "lightning",
  "psychic",
  "fighting",
  "darkness",
  "metal",
  "fairy",
  "dragon",
  "colorless",
] as const;

function looksLikeBasicEnergy(name: string): boolean {
  const lower = name.toLowerCase();
  if (!lower.includes("energy")) return false;
  if (
    lower.includes("special") ||
    lower.includes("double") ||
    lower.includes("rapid")
  ) {
    return false;
  }
  return BASIC_ENERGY_TYPES.some((t) => lower.includes(t));
}

function extractEnergyType(name: string): string | undefined {
  const lower = name.toLowerCase();
  for (const type of BASIC_ENERGY_TYPES) {
    if (lower.includes(type)) {
      return type[0].toUpperCase() + type.slice(1);
    }
  }
  return undefined;
}

function cardLooksLikeEnergy(card: Card, energyType: string): boolean {
  const cardName = card.name.toLowerCase();
  const energyLower = energyType.toLowerCase();
  const isEnergyCard =
    card.tcgSupertype?.toLowerCase() === "energy" ||
    cardName.includes("energy");
  if (!isEnergyCard) return false;
  if (cardName.includes(energyLower)) return true;
  if (card.tcgTypes?.some((t) => t.toLowerCase() === energyLower)) return true;
  return false;
}
